import { createElement } from 'react';

import { type IntlShape } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';

import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { type EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, type DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import type { BlockControlsPlugin } from '../blockControlsPluginType';
import { QuickInsertWithVisibility } from '../ui/quick-insert-button';

import { type AnchorRectCache } from './utils/anchor-utils';
import { getActiveBlockMarks } from './utils/marks';
import { createVanillaButton } from './vanilla-quick-insert';

const TYPE_QUICK_INSERT = 'INSERT_BUTTON';

export const findQuickInsertInsertButtonDecoration = (
	decorations: DecorationSet,
	from?: number,
	to?: number,
) => {
	return decorations.find(from, to, (spec) => spec.type === TYPE_QUICK_INSERT);
};

type QuickInsertButtonDecorationParams = {
	anchorName: string;
	anchorRectCache?: AnchorRectCache;
	api: ExtractInjectionAPI<BlockControlsPlugin>;
	editorState: EditorState;
	formatMessage: IntlShape['formatMessage'];
	nodeType: string;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	rootAnchorName?: string;
	rootNodeType?: string;
	rootPos: number;
};

export const quickInsertButtonDecoration = ({
	api,
	formatMessage,
	rootPos,
	anchorName,
	nodeType,
	nodeViewPortalProviderAPI,
	rootAnchorName,
	rootNodeType,
	anchorRectCache,
	editorState,
}: QuickInsertButtonDecorationParams) => {
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const key = uuid();
	const cleanupCallbacks: (() => void)[] = [];

	const widgetSpec = editorExperiment('platform_editor_breakout_resizing', true)
		? {
				side: -2,
				type: TYPE_QUICK_INSERT,
				/**
				 * sigh - `marks` influences the position that the widget is drawn (as described on the `side` property).
				 * Exclude 'breakout' on purpose, so the widgets render at the top of the document to avoid z-index issues
				 * Other block marks must be added, otherwise PM will split the DOM elements causing mutations and re-draws
				 */

				marks: fg('platform_editor_breakout_resizing_widget_fix')
					? getActiveBlockMarks(editorState, rootPos)
					: [],
				destroy: (_: Node) => {
					if (fg('platform_editor_fix_widget_destroy')) {
						nodeViewPortalProviderAPI.remove(key);
					}
					cleanupCallbacks.forEach((cb) => {
						cb();
					});
				},
			}
		: {
				side: -2,
				type: TYPE_QUICK_INSERT,
				destroy: (_: Node) => {
					if (fg('platform_editor_fix_widget_destroy')) {
						nodeViewPortalProviderAPI.remove(key);
					}
					cleanupCallbacks.forEach((cb) => {
						cb();
					});
				},
			};

	return Decoration.widget(
		rootPos,
		(view, getPos) => {
			const element = document.createElement('span');
			// inline decoration causes cursor disappear when focusing editor at the first line (e.g. press Tab when title is focused)
			element.style.display = 'block';

			if (expValEquals('platform_editor_quick_insert_image_wrap_right_fix', 'isEnabled', true)) {
				// make sure it does not interfere with elements floating next to each other e.g. paragraph next to image with wrap-right
				element.style.clear = 'unset';
			}
			element.contentEditable = 'false';
			element.setAttribute('data-blocks-quick-insert-container', 'true');
			element.setAttribute('data-testid', 'block-ctrl-quick-insert-button');
			if (
				editorExperiment('platform_editor_block_control_optimise_render', true, { exposure: true })
			) {
				const vanillaElement = createVanillaButton({
					formatMessage,
					api,
					view,
					getPos,
					cleanupCallbacks,
					rootAnchorName: rootAnchorName ?? nodeType,
					anchorName,
					rootNodeType: rootNodeType ?? nodeType,
					anchorRectCache,
				});
				element.appendChild(vanillaElement);
				return element;
			}

			nodeViewPortalProviderAPI.render(
				() =>
					createElement(QuickInsertWithVisibility, {
						api,
						getPos,
						formatMessage,
						view,
						nodeType,
						anchorName,
						rootAnchorName,
						rootNodeType: rootNodeType ?? nodeType,
						anchorRectCache,
					}),
				element,
				key,
				undefined,
				// @portal-render-immediately
				true,
			);

			return element;
		},
		widgetSpec,
	);
};
